import React, { useEffect, useMemo, useState } from "react";

export const SupportResultReportComponent = () => {
    const user = JSON.parse(localStorage.getItem("userData"));
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null); // object เดียวจาก data[0]
    const [error, setError] = useState("");

    const total = report?.totalSupport ?? 0;
    const rows = report?.bySupportType ?? [];

    const uniqueTypeCount = useMemo(() => rows.length, [rows]);

    const rowsWithPercent = useMemo(() => {
        if (!total) return rows.map(r => ({ ...r, percent: 0 }));
        return rows.map(r => ({
            ...r,
            percent: ((r.count / total) * 100)
        }));
    }, [rows, total]);

    const fetchReport = async () => {
        try {
            setLoading(true);
            setError("");
            const res = await fetch("/api/SupportReports/supportResultReport", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": user.token
                },
                body: JSON.stringify({}) // ไม่ต้องส่งอะไร เพราะ dept มาจาก token
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `HTTP ${res.status}`);
            }

            const json = await res.json();
            // บางระบบ data อาจเป็น object เดียว ไม่ใช่อาเรย์ — แต่จากโค้ดฝั่ง backend ที่คุยกันจะเป็น object เดียว
            // ในคำตอบก่อนหน้า เราห่อเป็น object เดียว: { totalSupport, bySupportType }
            // แต่หากคุณห่อเป็นลิสต์ ให้ดึง index 0
            const payload = Array.isArray(json?.dataenum) ? json.dataenum[0] : json?.dataenum;

            setReport(payload || { totalSupport: 0, bySupportType: [] });
        } catch (err) {
            console.error(err);
            setError(err?.message || "เกิดข้อผิดพลาดขณะดึงข้อมูล");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const exportCSV = () => {
        const header = ["SupportTypeId", "SupportTypeName", "Count", "Percent(%)"];
        const lines = rowsWithPercent.map(r => [
            r.supportTypeId,
            (r.supportTypeName ?? "").toString().replace(/"/g, '""'),
            r.count,
            (Math.round(r.percent * 100) / 100).toFixed(2)
        ]);

        const csv =
            header.join(",") +
            "\n" +
            lines.map(arr =>
                arr
                    .map(v => (typeof v === "string" ? `"${v}"` : v))
                    .join(",")
            ).join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "support-result-report.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="container py-4" style={{ maxWidth: 960 }}>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 className="m-0">สรุปจำนวนคำขอของหน่วยงาน</h3>
                <div className="d-flex gap-2">
                    <button className="btn btn-outline-secondary" onClick={fetchReport} disabled={loading}>
                        {loading ? "กำลังดึงข้อมูล..." : "Refresh"}
                    </button>
                    <button className="btn btn-success" onClick={exportCSV} disabled={!rows.length}>
                        Export CSV
                    </button>
                </div>
            </div>

            {/* Error */}
            {error ? (
                <div className="alert alert-danger">{error}</div>
            ) : null}

            {/* Summary cards */}
            <div className="row g-3 mb-4">
                <div className="col-12 col-md-6">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="text-muted small">จำนวนคำขอทั้งหมด</div>
                            <div className="display-6 fw-bold">{total.toLocaleString()}</div>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="text-muted small">จำนวนประเภทคำขอ</div>
                            <div className="display-6 fw-bold">{uniqueTypeCount.toLocaleString()}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card shadow-sm border-0">
                <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <h5 className="m-0">รายละเอียดแยกตามประเภท</h5>
                        <div className="text-muted small">
                            {new Date().toLocaleString()}
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th>ประเภท</th>
                                    <th style={{ width: 140 }} className="text-end">จำนวนคำขอ</th>
                                    <th style={{ width: 160 }} className="text-end">Percent</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!loading && rowsWithPercent.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="text-center text-muted py-4">
                                            ไม่พบข้อมูล
                                        </td>
                                    </tr>
                                ) : (
                                    rowsWithPercent.map((r) => (
                                        <tr key={r.supportTypeId}>
                                            {/* <td>{r.supportTypeId}</td> */}
                                            <td>{r.supportTypeName || "-"}</td>
                                            <td className="text-end">{r.count.toLocaleString()}</td>
                                            <td className="text-end">
                                                {(Math.round(r.percent * 100) / 100).toFixed(2)}%
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                            <tfoot className="table-light">
                                {rowsWithPercent.length > 0 && (
                                    <tr>
                                        <td className="text-end">รวม</td>
                                        <td className="text-end">{total.toLocaleString()}</td>
                                        <td className="text-end">100%</td>
                                    </tr>

                                )}
                            </tfoot>

                        </table>
                    </div>

                    {/* Progress list (สวย ๆ เพิ่มความเข้าใจ) */}
                    {rowsWithPercent.length > 0 && (
                        <div className="mt-4">
                            {rowsWithPercent.map((r) => (
                                <div key={`bar-${r.supportTypeId}`} className="mb-2">
                                    <div className="d-flex justify-content-between small">
                                        <span>
                                            {r.supportTypeName || `ประเภท ${r.supportTypeId}`}
                                        </span>
                                        <span>
                                            {r.count.toLocaleString()} รายการ · {(Math.round(r.percent * 100) / 100).toFixed(2)}%
                                        </span>
                                    </div>
                                    <div className="progress" style={{ height: 8 }}>
                                        <div
                                            className="progress-bar"
                                            role="progressbar"
                                            style={{ width: `${Math.min(r.percent, 100)}%` }}
                                            aria-valuenow={r.percent}
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Loading overlay แบบง่าย */}
            {loading && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(255,255,255,0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999
                    }}
                >
                    <div className="spinner-border" role="status" />
                    <span className="ms-2">กำลังโหลดข้อมูล...</span>
                </div>
            )}
        </div>
    );
};
